import { select } from "starfx";

import { schemaB } from "@app/stateCommon/schemaBlue";
import {
  isEpBuilderAll,
  isEpBuilderAnyIssuer,
  isEpBuilderAnyIssuerNoSlices,
} from "@app/statefx/routines/mapperfn-pathbuilder";
import { schema } from "@app/statefx/schema";
import { selCountOfShard } from "@app/statefx/selectors/ds-swr";
import { shardBuilder } from "@app/statefx/workers/helpers";
import { BlueItem, TBlueCommon, TStringInterval } from "@app/types/insider";

import type { TSChemaBlue } from "@app/stateCommon/schemaBlue";
import type { Operation } from "starfx";
import type { AppState } from "../xstore";
function hasBootPriority(obj: any): obj is { bootPriority: number } {
  return "bootPriority" in obj;
}
function hasUrlPathBuilder(obj: any): obj is { urlPathBuilder: any } {
  return "urlPathBuilder" in obj;
}

function hasApiUrl(obj: any): obj is { apiUrl: string } {
  return "apiUrl" in obj;
}
function hasPkeyUrl(obj: any): obj is { pkeyUrl: string } {
  return "pkeyUrl" in obj;
}

function hasAppContext(obj: any): obj is { appContext: string[] } {
  return "appContext" in obj;
}
const maxTrack_ = () =>
  Object.values(schemaB).reduce((acc, val) => {
    if (hasBootPriority(val)) {
      if (val.bootPriority > acc) {
        return val.bootPriority;
      }
    }
    return acc;
  }, 0);

// const bcMsg = (name, url, shid0 = null, shid1 = null) =>
//   JSON.stringify({
//     type: 'GET',
//     url: 'runner/warmup3',
//     options: {
//       params: url,
//       meta: { key: name, shid0 },
//     },
//   });
type TShardRequest = {
  type: "GET" | "POST" | "PUT" | "DELETE";
  url: "runner/csdb" | "runner/vacache";
  options: {
    params: string;
    meta: {
      key: string;
      shid0: string | null;
      shid1: string | null;
      expectedShardCount: number;
      storeHKey: string;
      storeHValue: string;
      storeShardCount: number;
    };
  };
};
export const csdbMsg = (
  name,
  url,
  shid0 = null,
  shid1 = null,
  shardStatistics = { storeHKey: "", storeHValue: "", storeShardCount: 0 }
) =>
  JSON.stringify({
    type: "GET",
    url: "runner/vacache",
    options: {
      params: url,
      meta: {
        key: name,
        shid0,
        shid1,
        expectedShardCount: shardStatistics.storeShardCount,
        storeHKey: shardStatistics.storeHKey,
        storeHValue: shardStatistics.storeHValue,
        storeShardCount: shardStatistics.storeShardCount,
      },
    },
  } as TShardRequest);

// Client Side Database Commander.
//// -- then  throttle api ///
/// then study the heatmap api ///

//"shard" = "ciob"//
// shid0 = by recent use | by last use -- daily value ... -- it has to be reset every day -- not good
// shid1 = by interval
// shid2 = by lexigraphic
// shid3 = by logical grouping
// shid4 =  ... ?
/* 

i.e. the simle primary key is a particular case of a shard.

a record can have multiple shids,

when we update a shid, we delete the old shard by id and insert the new shard by id
so, we can index our shards for fast retrieval

operations to take care about:
-- creating shards.
-- retrieving shards.
-- deleting shards.

*/

// we run the full db on that:
export const vacacheApiFactory = function* (): Operation<string[]> {
  const app = yield* select(schema.app.select);
  const absoluteStart = app.absolutestart;
  const slicesUnShided = shardBuilder(new Date(absoluteStart));
  const fullSchemaKeys = Object.keys(schemaB) as (keyof TSChemaBlue)[];
  const result = yield* recacheKeys(fullSchemaKeys, slicesUnShided);
  return result;
};

// cherry pick the tables to recache
export const recacheKeys = function* (
  targetTables: (keyof TSChemaBlue)[],
  targetSlices: TStringInterval[]
): Operation<string[]> {
  const appIsPersonalKarma_ = yield* select(schema.whoami.select);
  const appIsPersonalKarma = appIsPersonalKarma_.AppIsPersonalKarma;

  const slices = targetSlices.map((shard, idx) => {
    return {
      start: shard.start,
      end: shard.end,
      shid0: idx <= 1 ? `${shard.start}-${shard.end}` : null, // mantain the bootup 2 shards 0,1
      shid1: `${shard.start}-${shard.end}`,
    };
  });

  const emitent_ = yield* select(schema.invoiceIssuer.selectTableAsList);
  const emitenti = emitent_.map((x) => x.ABR);
  const ama_ = yield* select(schema.whoami.select);
  const ama = ama_.AmaId;

  const schemaBfiltered = Object.values(schemaB).filter((x: BlueItem) => {
    return (
      targetTables.includes(x.name as keyof TSChemaBlue) &&
      x.idb &&
      hasApiUrl(x) &&
      !!x.apiUrl &&
      hasBootPriority(x) &&
      x.bootPriority >= 0 &&
      hasUrlPathBuilder(x) &&
      x["oncsdb"] !== "no" &&
      hasAppContext(x) &&
      (appIsPersonalKarma
        ? x.appContext.includes("CtxPersonalKarma")
        : x.appContext.includes("CtxJurnalCorporate"))
    );
  });

  /// filter out the actions that have priority but not iterates over slices;
  const onceOverall = Object.values(schemaBfiltered).reduce((acc, val) => {
    if (
      isEpBuilderAnyIssuerNoSlices(val) ||
      !hasUrlPathBuilder(val) ||
      val.urlPathBuilder === null
    ) {
      if (acc[val.bootPriority]) {
        acc[val.bootPriority].push(val);
      } else {
        acc[val.bootPriority] = [val];
      }
    }
    return acc;
  }, {});

  /// and the ones that have the urlPathBuilder but not the issuer
  const oncePerSlice = Object.values(schemaBfiltered).reduce((acc, val) => {
    if (isEpBuilderAnyIssuer(val)) {
      if (acc[val.bootPriority]) {
        acc[val.bootPriority].push(val);
      } else {
        acc[val.bootPriority] = [val];
      }
    }
    return acc;
  }, {});

  /// the ones that have the urlPathBuilder and the issuer, iteates over all slices and all issuers
  const eachSliceAndIssuer = Object.values(schemaBfiltered).reduce(
    (acc, val) => {
      if (isEpBuilderAll(val)) {
        if (acc[val.bootPriority]) {
          acc[val.bootPriority].push(val);
        } else {
          acc[val.bootPriority] = [val];
        }
      }
      return acc;
    },
    {}
  );

  const messageStack = []; //
  const i = 0;
  let maxTrack = maxTrack_();
  while (maxTrack >= 0) {
    //run the onceOverall
    if (onceOverall[maxTrack]) {
      act: for (const action of onceOverall[maxTrack]) {
        const urlRoot = action.apiUrl;
        if (!urlRoot) continue act;

        // todo: https://github.com/VldMrgnn/jk-react-app/issues/117 insert oncsb | onmru logic
        // if (action.oncsdb === 'ifempty') {
        //   // const ds = yield* select(schema[action.name].select);
        //   if (isTBuleEmpty(action.name)) {
        //     continue act;
        //   }
        // }

        const urls =
          hasUrlPathBuilder(action) && action.urlPathBuilder
            ? action.urlPathBuilder(urlRoot, [], [], ama)
            : [urlRoot];

        for (const url of urls) {
          // We will compare with the shard in idb.
          const shcount = yield* select((s: AppState) =>
            selCountOfShard(s, { objectName: action.name, shid1: "nop" })
          );

          const message = csdbMsg(action.name, url, "nop", "nop", shcount);
          messageStack.push(message);
        }
      }
    }
    //run the oncePerSlice
    for (const slice of slices) {
      if (oncePerSlice[maxTrack]) {
        act: for (const action of oncePerSlice[maxTrack]) {
          const urlRoot = action.apiUrl;
          if (!urlRoot) continue act;

          // todo: insert oncsb | onmru logic

          const urls =
            hasUrlPathBuilder(action) && action.urlPathBuilder
              ? action.urlPathBuilder(urlRoot, [], [slice], ama)
              : [urlRoot];

          for (const url of urls) {
            const shcount = yield* select((s: AppState) =>
              selCountOfShard(s, {
                objectName: action.name,
                shid1: slice.shid1,
              })
            );

            const message = csdbMsg(
              action.name,
              url,
              slice.shid0,
              slice.shid1,
              shcount
            );
            messageStack.push(message);
          }
        }
      }
    }
    //run the eachSliceAndIssuer
    for (const slice of slices) {
      for (const emitent of emitenti) {
        if (eachSliceAndIssuer[maxTrack]) {
          act: for (const action of eachSliceAndIssuer[maxTrack]) {
            const urlRoot = action.apiUrl;
            if (!urlRoot) continue act;

            // todo: insert oncsb | onmru logic (oncsb === ifEmpty then ...)

            const urls =
              hasUrlPathBuilder(action) && action.urlPathBuilder
                ? action.urlPathBuilder(urlRoot, [emitent], [slice], ama)
                : [urlRoot];
            for (const url of urls) {
              const shcount = yield* select((s: AppState) =>
                selCountOfShard(s, {
                  objectName: action.name,
                  shid1: `${slice.shid1}:${emitent}`,
                })
              );
              const message = csdbMsg(
                action.name,
                url,
                slice.shid0,
                `${slice.shid1}:${emitent}`,
                shcount
              );
              messageStack.push(message);
            }
          }
        }
      }
    }
    maxTrack--;
  }

  return messageStack;
};
